import { subDays } from "date-fns"
import { createSlice, isAnyOf } from "@reduxjs/toolkit"
import type { Reducer } from "@reduxjs/toolkit"

import * as AppStorage from "@/base/async-storage"
import { rejectAfterTimeout } from "@/base/promise-timeout"
import { sendRequest } from "@/base/send-request"
import { createAsyncThunk } from "@/redux/create-async-thunk"
import type * as Models from "@/redux/models"
import { getDemoFarmIdOrActiveFarmIdFromState } from "@/redux/selectors"
import {
  HEATMAP_CACHE_STALE_MS,
  HEATMAP_REQUEST_TIMEOUT_MS,
} from "@/redux/status-map-heatmap.reducer"

import type { RootState } from "./root.reducer"

const DAYS_BACK = 7

interface StatusMapPivotHeatmapState {
  /** Session cache: pivotId → heatmap grid. Cleared on farm switch. */
  cache: Record<number, Models.PivotIrrigationHeatmapGrid>
  /** pivotId → epoch ms of the last successful fetch (staleness check). */
  fetchedAtMs: Record<number, number>
  /** Pivot IDs currently being fetched */
  loadingIds: number[]
}

const initialState: StatusMapPivotHeatmapState = {
  cache: {},
  fetchedAtMs: {},
  loadingIds: [],
}

/**
 * Load the last 7 days of radial heatmap data for a single center pivot.
 * Silently skips if the pivot was fetched recently or is currently loading.
 * The staleness window is how a finished pivot plan reaches the map
 * mid-session — pivots have no equivalent of the reel-run completion hook.
 */
export const loadStatusMapPivotHeatmapAsync = createAsyncThunk<
  { pivotId: number; grid: Models.PivotIrrigationHeatmapGrid },
  number
>(
  "statusMapPivotHeatmap/loadPivot",
  async (pivotId, { getState }) => {
    const state = getState()
    const activeFarmId = getDemoFarmIdOrActiveFarmIdFromState(state)
    const maxDateMs = Date.now()
    const minDateMs = subDays(new Date(), DAYS_BACK).getTime()
    const grid = await rejectAfterTimeout(
      sendRequest("LoadCenterPivotIrrigationHeatmap", {
        activeFarmId,
        actionArguments: {
          pivotId,
          options: { maxDateMs, minDateMs },
        },
      }),
      HEATMAP_REQUEST_TIMEOUT_MS,
      `LoadCenterPivotIrrigationHeatmap timed out for pivot ${pivotId}`,
    )
    return { pivotId, grid }
  },
  {
    condition: (pivotId, { getState }) => {
      const state = getState() as RootState
      const { cache, fetchedAtMs, loadingIds } = state.statusMapPivotHeatmap
      if (loadingIds.includes(pivotId)) {
        return false
      }
      if (!(pivotId in cache)) {
        return true
      }
      const fetchedAt = fetchedAtMs[pivotId]
      return (
        fetchedAt === undefined ||
        Date.now() - fetchedAt > HEATMAP_CACHE_STALE_MS
      )
    },
  },
)

const slice = createSlice({
  extraReducers: (builder) =>
    builder
      .addCase(loadStatusMapPivotHeatmapAsync.pending, (state, { meta }) => {
        state.loadingIds.push(meta.arg)
      })
      .addCase(loadStatusMapPivotHeatmapAsync.fulfilled, (state, { payload }) => {
        state.cache[payload.pivotId] = payload.grid
        state.fetchedAtMs[payload.pivotId] = Date.now()
        state.loadingIds = state.loadingIds.filter((id) => id !== payload.pivotId)
      })
      .addCase(loadStatusMapPivotHeatmapAsync.rejected, (state, { meta }) => {
        state.loadingIds = state.loadingIds.filter((id) => id !== meta.arg)
      })
      .addMatcher(
        isAnyOf(AppStorage.setActiveFarmId.fulfilled),
        (state) => {
          state.cache = {}
          state.fetchedAtMs = {}
          state.loadingIds = []
        },
      ),
  initialState,
  name: "statusMapPivotHeatmap",
  reducers: {},
})

const statusMapPivotHeatmap: Reducer<StatusMapPivotHeatmapState> = slice.reducer
export default statusMapPivotHeatmap

export function getStatusMapPivotHeatmapCache(state: RootState) {
  return state.statusMapPivotHeatmap.cache
}

/** Whether any pivot heatmap request is currently in flight. */
export function getStatusMapPivotHeatmapLoading(state: RootState) {
  return state.statusMapPivotHeatmap.loadingIds.length > 0
}
